
<template>
  <div class="showcase-container">
    <!-- 3D Canvas -->
    <TresCanvas v-bind="gl" class="canvas">
      <TresPerspectiveCamera :position="[0, 0, 8]" />
      <OrbitControls 
        :enable-zoom="false" 
        :enable-pan="false"
        :enable-rotate="true"
        :auto-rotate="false"
        :max-polar-angle="Math.PI / 2"
        :min-polar-angle="Math.PI / 2"
      />
      
      <!-- Lighting -->
      <TresAmbientLight :intensity="0.4" />
      <TresDirectionalLight 
        :position="[5, 5, 5]" 
        :intensity="0.8"
      />
      
      <!-- Project Cards with key to force re-render -->
      <ProjectCard3D
        v-for="(project, index) in projects"
        :key="`card-${project.id}-${currentIndex}`"
        :project="project"
        :index="index"
        :total="projects.length"
        :current-index="currentIndex"
        :position="getCardPosition(index)"
        :rotation="getCardRotation(index)"
        :scale="getCardScale(index)"
        @click="selectProject(index)"
      />
    </TresCanvas>

    <!-- HTML Content Overlay -->
    <div class="cards-overlay">
      <div
        v-for="(project, index) in projects"
        :key="`overlay-${project.id}`"
        class="card-overlay"
        :class="{ active: index === currentIndex }"
        :style="getOverlayStyle(index)"
        @click="selectProject(index)"
      >
        <!-- Your existing card content -->
        <div class="card-content" :style="{ borderColor: project.color }">
          <!-- Project Image -->
          <div class="card-image-container">
            <img 
              v-if="project.image" 
              :src="project.image" 
              :alt="project.name"
              class="card-image"
              @error="handleImageError"
              @load="handleImageLoad"
            />
            <div v-else class="card-image-placeholder" :style="{ backgroundColor: project.color }">
              <svg class="placeholder-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21,19V5c0,-1.1 -0.9,-2 -2,-2H5c-1.1,0 -2,0.9 -2,2v14c0,1.1 0.9,2 2,2h14c1.1,0 2,-0.9 2,-2zM8.5,13.5l2.5,3.01L14.5,12l4.5,6H5l3.5,-4.5z"/>
              </svg>
            </div>
            <div class="card-image-overlay" :style="{ background: `linear-gradient(to bottom, transparent 0%, ${project.color}20 100%)` }"></div>
          </div>
          
          <!-- Project Content -->
          <div class="card-text-content">
            <h3>{{ project.name }}</h3>

          </div>
        </div>
      </div>
    </div>

    <!-- UI Overlay -->
    <div class="ui-overlay">
      <!-- Project Info Panel -->
      <div class="project-info" :class="{ active: selectedProject }">
        <div v-if="selectedProject" class="info-content">
          <!-- Project Header with Image -->
          <div class="info-header">
            <div class="info-image-container">
              <img 
                v-if="selectedProject.image" 
                :src="selectedProject.image" 
                :alt="selectedProject.name"
                class="info-image"
              />
              <div v-else class="info-image-placeholder" :style="{ backgroundColor: selectedProject.color }">
                <svg class="placeholder-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21,19V5c0,-1.1 -0.9,-2 -2,-2H5c-1.1,0 -2,0.9 -2,2v14c0,1.1 0.9,2 2,2h14c1.1,0 2,-0.9 2,-2zM8.5,13.5l2.5,3.01L14.5,12l4.5,6H5l3.5,-4.5z"/>
                </svg>
              </div>
            </div>
            <div class="info-text">
              <h2>{{ selectedProject.name }}</h2>
              <div class="project-meta">
                <span class="project-type" :style="{ color: selectedProject.color }">
                  {{ getProjectType(selectedProject.tags[0]) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Project Description -->
          <div class="info-body">
            <p>{{ selectedProject.description }}</p>
            
            <!-- Project Stats -->
            <div class="project-stats" v-if="selectedProject.stats">
              <div class="stat-item" v-for="(value, key) in selectedProject.stats" :key="key">
                <span class="stat-label">{{ formatStatLabel(key) }}</span>
                <span class="stat-value" :style="{ color: selectedProject.color }">{{ value }}</span>
              </div>
            </div>

            <!-- Tags -->
            <div class="tags">
              <span 
                v-for="tag in selectedProject.tags" 
                :key="tag" 
                class="tag"
                :style="{ backgroundColor: selectedProject.color }"
              >
                {{ tag }}
              </span>
            </div>

            <!-- Action Buttons -->
            <div class="action-buttons">
              <button 
                class="demo-btn primary"
                :style="{ backgroundColor: selectedProject.color }"
                @click="openDemo(selectedProject.demoUrl)"
              >
                <svg class="btn-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8,5.14V19.14L19,12.14L8,5.14Z" />
                </svg>
                View Demo
              </button>
              <button 
                v-if="selectedProject.githubUrl"
                class="demo-btn secondary"
                @click="openDemo(selectedProject.githubUrl)"
              >
                <svg class="btn-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,2A10,10 0 0,0 2,12C2,16.42 4.87,20.17 8.84,21.5C9.34,21.58 9.5,21.27 9.5,21C9.5,20.77 9.5,20.14 9.5,19.31C6.73,19.91 6.14,17.97 6.14,17.97C5.68,16.81 5.03,16.5 5.03,16.5C4.12,15.88 5.1,15.9 5.1,15.9C6.1,15.97 6.63,16.93 6.63,16.93C7.5,18.45 8.97,18 9.54,17.76C9.63,17.11 9.89,16.67 10.17,16.42C7.95,16.17 5.62,15.31 5.62,11.5C5.62,10.39 6,9.5 6.65,8.79C6.55,8.54 6.2,7.5 6.75,6.15C6.75,6.15 7.59,5.88 9.5,7.17C10.29,6.95 11.15,6.84 12,6.84C12.85,6.84 13.71,6.95 14.5,7.17C16.41,5.88 17.25,6.15 17.25,6.15C17.8,7.5 17.45,8.54 17.35,8.79C18,9.5 18.38,10.39 18.38,11.5C18.38,15.32 16.04,16.16 13.81,16.41C14.17,16.72 14.5,17.33 14.5,18.26C14.5,19.6 14.5,20.68 14.5,21C14.5,21.27 14.66,21.59 15.17,21.5C19.14,20.16 22,16.42 22,12A10,10 0 0,0 12,2Z" />
                </svg>
                GitHub
              </button>
            </div>
          </div>
        </div>
      </div>

  <div class="navigation">
    <button 
      class="nav-btn prev" 
      @click="previousProject"
      title="Previous project"
    >
      ←
    </button>
    
    <div class="indicators">
      <span
        v-for="(project, index) in projects"
        :key="index"
        class="indicator"
        :class="{ active: index === currentIndex }"
        :style="{ backgroundColor: index === currentIndex ? project.color : '' }"
        @click="goToProject(index)"
        :title="project.name"
      >
        <img 
          v-if="project.image" 
          :src="project.image" 
          :alt="project.name"
          class="indicator-image"
        />
      </span>
    </div>
    
    <button 
      class="nav-btn next" 
      @click="nextProject"
      title="Next project"
    >
      →
    </button>
    
    <!-- Auto-play toggle button -->
    <button 
      class="nav-btn auto-play" 
      @click="toggleAutoPlay"
      :class="{ active: autoPlayEnabled }"
      :title="autoPlayEnabled ? 'Pause auto-play' : 'Start auto-play'"
    >
      <svg v-if="autoPlayEnabled" class="btn-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14,19H18V5H14M6,19H10V5H6V19Z" />
      </svg>
      <svg v-else class="btn-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8,5.14V19.14L19,12.14L8,5.14Z" />
      </svg>
    </button>
  </div>

      <!-- Image Modal -->
      <div v-if="showImageModal" class="image-modal" @click="closeImageModal">
        <div class="modal-content" @click.stop>
          <button class="modal-close" @click="closeImageModal">×</button>
          <img :src="modalImage" :alt="selectedProject?.name" class="modal-image" />
          <div class="modal-info">
            <h3>{{ selectedProject?.name }}</h3>
            <p>{{ selectedProject?.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { TresCanvas, TresPerspectiveCamera, TresAmbientLight, TresDirectionalLight } from '@tresjs/core'
import { OrbitControls } from '@tresjs/cientos'
import ProjectCard3D from '@/components/Projects/ProjectCard3D.vue'
import nottwitterImg from '@/assets/nottwitter.png'

export default {
  name: 'ProjectShowcase',
  components: {
    TresCanvas,
    TresPerspectiveCamera,
    TresAmbientLight,
    TresDirectionalLight,
    OrbitControls,
    ProjectCard3D
  },
  setup() {
    // Canvas configuration
    const gl = {
      clearColor: '#1a1a2e',
      shadows: false,
      alpha: false,
      antialias: true
    }

    // Reactive data
    const currentIndex = ref(0)
    const isAnimating = ref(false)
    const showImageModal = ref(false)
    const modalImage = ref('')
    const autoPlayInterval = ref(null)
    const autoPlayEnabled = ref(true)
    const autoPlayDelay = 4000 // 4 seconds

    // Enhanced project data with images
const projects = ref([
  {
    id: 1,
    name: 'Barber Shop Website',
    description: 'A local barber shop website in Würzburg featuring modern design and booking functionality. Note: I was only responsible for the code implementation.',
    tags: ['HTML', 'CSS', 'JavaScript', 'PHP'],
    demoUrl: 'https://alowlaomar.de/bayrischer-barbier',
    githubUrl: 'https://github.com/example/barbershop',
    image: 'https://alowlaomar.de/Media/bb.jpg',
    color: '#102a3b',
    stats: {
      Time: '3H',
      Started: '2022',
      Takeaways: '20%'
    }
  },
  {
    id: 2,
    name: 'E-Commerce Platform',
    description: 'A full-stack e-commerce solution built with native PHP for high-quality design assets. Originally developed for a startup that unfortunately ceased operations.',
    tags: ['PHP', 'jQuery', 'HTML', 'CSS', 'MySQL'],
    demoUrl: 'https://alowlaomar.de/TurboDesigners/',
    githubUrl: 'https://github.com/Qabotech/TurboDesigners/',
    image: 'https://alowlaomar.de/Media/TD.png',
    color: '#f25861',
    stats: {
      Time: '60H',
      Started: '2020',
      Takeaways: '80%'
    }
  },
  {
    id: 3,
    name: 'Laravel Blog Platform',
    description: 'A Laravel-based blog developed with Laravel Nova admin panel, Tailwind CSS, and jQuery. Built to deepen knowledge of Laravel Nova and advanced Laravel features.',
    tags: ['Laravel', 'Laravel Nova', 'Tailwind CSS', 'jQuery', 'PHP'],
    demoUrl: 'https://blog.alowlaomar.de/',
    githubUrl: 'https://github.com/example/laravel-blog',
    image: 'https://alowlaomar.de/AlowlaOmar/imgs/blog.png',
    color: '#FF2D20',
    stats: {
      Time: '50H',
      Started: '2022',
      Takeaways: '100%'
    }
  },
  {
    id: 4,
    name: 'Image to Player Game',
    description: 'A fun game project combining PHP native image processing with Imagick extension and JavaScript game logic for dynamic visual effects and interactive gameplay.',
    tags: ['PHP', 'Imagick', 'JavaScript', 'CSS', 'Game Development'],
    demoUrl: 'https://alowlaomar.de/ImgtoPlayer/',
    githubUrl: 'https://github.com/example/img-to-player',
    image: 'https://alowlaomar.de/Media/imgtoplayer.jpg',
    color: '#c471ed',
    stats: {
      Time: '2H',
      Started: '2023',
      Takeaways: '10%'
    }
  },
  {
    id: 5,
    name: 'Instagram Clone',
    description: 'A social media platform built with native PHP, JavaScript, and CSS as a school project. Completed in just one week with features like image upload, profiles, and posts.',
    tags: ['PHP', 'JavaScript', 'CSS', 'MySQL', 'Social Media'],
    demoUrl: 'https://alowlajavaproject.wuaze.com/sys/',
    githubUrl: 'https://github.com/example/instagram-clone',
    image: 'https://alowlaomar.de/Media/omargram.jpg',
    color: '#E4405F',
    stats: {
      Time: '20H',
      Started: '2022',
      Takeaways: '80%'
    }
  },
  {
    id: 6,
    name: 'About Islam Website',
    description: 'A simple informational website about Islam developed for a student project {[i did the coding only]}. Built with HTML, CSS, and JavaScript for clear and user-friendly information presentation.',
    tags: ['HTML', 'CSS', 'JavaScript', 'Educational'],
    demoUrl: 'https://alowlaomar.de/ULI/',
    githubUrl: 'https://github.com/Qabotech/ULI',
    image: 'https://alowlaomar.de/Media/uli.jpg',
    color: '#2E8B57',
    stats: {
      Time: '10H',
      Started: '2021',
      Takeaways: '5%'
    }
  },
  {
    id: 7,
    name: 'Subnet Calculator',
    description: 'A networking tool developed to help classmates understand subnetting concepts. Built with JavaScript for calculations and CSS for a clean, user-friendly interface.',
    tags: ['JavaScript', 'CSS', 'HTML', 'Networking', 'Educational'],
    demoUrl: 'https://subnetingiuk2fia10.netlify.app/',
    githubUrl: 'hhttps://github.com/Qabotech/Subnetting',
    image: 'https://alowlaomar.de/Media/subnet.png',
    color: '#1976D2',
    stats: {
      Time: '1H',
      Started: '2022',
      Takeaways: '100%'
    }
  },
  {
    id: 8,
    name: 'WebRTC Group Call App',
    description: 'A real-time group calling application using WebRTC and WebSocket technology. Built with JavaScript and jQuery for dynamic UI and seamless group communication.',
    tags: ['WebRTC', 'WebSocket', 'JavaScript', 'jQuery', 'Socket.IO'],
    demoUrl: 'https://call.alowlaomar.de/',
    githubUrl: 'https://github.com/Qabotech/zoomQT',
    image: 'https://alowlaomar.de/Media/call.jpg',
    color: '#9C27B0',
    stats: {
      Time: '5H',
      Started: '2023',
      Takeaways: '100%'
    }
  },
{
  id: 1,
  name: 'NotTwitter',
  description: 'A Twitter clone built with Laravel, Laravel Nova, Tailwind CSS, and jQuery. Real-time updates and a dynamic, responsive UI for seamless interaction. Build your social network platform with ease.',
  tags: ['Laravel', 'Laravel Nova', 'Tailwind CSS', 'jQuery', 'Real-Time', 'Social Media'],
  demoUrl: 'https://nottwitter.alowlaomar.de/',
  githubUrl: 'https://github.com/Qabotech/nottwitter',
  image: nottwitterImg,
  color: '#1DA1F2',
  stats: {
    Time: '10H',
    Started: '2023',
    Takeaways: '70%'
  }
}

])

     // Computed properties
    const selectedProject = computed(() => projects.value[currentIndex.value])
    const totalProjects = computed(() => projects.value.length)

    // Infinite loop helper function
    const normalizeIndex = (index) => {
      const total = totalProjects.value
      return ((index % total) + total) % total
    }

    // Card positioning functions - Updated for infinite loop
    const getCardPosition = (index) => {
      const totalCards = totalProjects.value
      const normalizedCurrent = normalizeIndex(currentIndex.value)
      
      // Calculate the shortest distance considering the circular nature
      let distance = index - normalizedCurrent
      if (Math.abs(distance) > totalCards / 2) {
        distance = distance > 0 ? distance - totalCards : distance + totalCards
      }
      
      const angle = (distance * Math.PI * 2) / totalCards
      const radius = 4
      const x = Math.sin(angle) * radius
      const z = Math.cos(angle) * radius - 1
      const y = Math.abs(distance) * -0.3
      
      return [x, y, z]
    }

    const getCardRotation = (index) => {
      const normalizedCurrent = normalizeIndex(currentIndex.value)
      let distance = index - normalizedCurrent
      
      if (Math.abs(distance) > totalProjects.value / 2) {
        distance = distance > 0 ? distance - totalProjects.value : distance + totalProjects.value
      }
      
      const rotY = distance * 0.2
      return [0, rotY, 0]
    }

    const getCardScale = (index) => {
      const normalizedCurrent = normalizeIndex(currentIndex.value)
      let distance = Math.abs(index - normalizedCurrent)
      
      if (distance > totalProjects.value / 2) {
        distance = totalProjects.value - distance
      }
      
      const scale = Math.max(0.4, 1 - distance * 0.15)
      return [scale, scale, scale]
    }

    const getOverlayStyle = (index) => {
      const position = getCardPosition(index)
      const scale = getCardScale(index)[0]
      
      const x = (position[0] * 8) + 50
      const y = (-position[1] * 15) + 50
      const z = position[2]
      
      return {
        left: `${x}%`,
        top: `${y}%`,
        transform: `translate(-50%, -50%) scale(${scale})`,
        opacity: scale > 0.6 ? 1 : 0.4,
        zIndex: index === currentIndex.value ? 10 : Math.floor((1 - Math.abs(z)) * 5),
        pointerEvents: scale > 0.6 ? 'auto' : 'none',
        transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
      }
    }

    // Auto-play functionality
    const startAutoPlay = () => {
      if (!autoPlayEnabled.value) return
      
      stopAutoPlay()
      autoPlayInterval.value = setInterval(() => {
        if (!isAnimating.value && !showImageModal.value) {
          nextProject()
        }
      }, autoPlayDelay)
    }

    const stopAutoPlay = () => {
      if (autoPlayInterval.value) {
        clearInterval(autoPlayInterval.value)
        autoPlayInterval.value = null
      }
    }

    const toggleAutoPlay = () => {
      autoPlayEnabled.value = !autoPlayEnabled.value
      if (autoPlayEnabled.value) {
        startAutoPlay()
      } else {
        stopAutoPlay()
      }
    }

    // Navigation methods - Updated for infinite loop
    const selectProject = (index) => {
      if (!isAnimating.value) {
        const normalizedIndex = normalizeIndex(index)
        if (normalizedIndex !== currentIndex.value) {
          isAnimating.value = true
          currentIndex.value = normalizedIndex
          
          // Restart auto-play timer
          if (autoPlayEnabled.value) {
            startAutoPlay()
          }
          
          setTimeout(() => {
            isAnimating.value = false
          }, 800)
        }
      }
    }

    const nextProject = () => {
      const nextIndex = normalizeIndex(currentIndex.value + 1)
      selectProject(nextIndex)
    }

    const previousProject = () => {
      const prevIndex = normalizeIndex(currentIndex.value - 1)
      selectProject(prevIndex)
    }

    const goToProject = (index) => {
      selectProject(index)
    }

    // Pause auto-play on user interaction
    const handleUserInteraction = () => {
      if (autoPlayEnabled.value) {
        stopAutoPlay()
        // Restart after a delay
        setTimeout(() => {
          if (autoPlayEnabled.value && !showImageModal.value) {
            startAutoPlay()
          }
        }, 2000)
      }
    }

    // Updated navigation methods with user interaction handling
    const nextProjectWithPause = () => {
      handleUserInteraction()
      nextProject()
    }

    const previousProjectWithPause = () => {
      handleUserInteraction()
      previousProject()
    }

    const goToProjectWithPause = (index) => {
      handleUserInteraction()
      goToProject(index)
    }

    const openDemo = (url) => {
      window.open(url, '_blank')
    }

    // Utility functions
    const truncateText = (text, length) => {
      return text.length > length ? text.substring(0, length) + '...' : text
    }

    const getProjectType = (tag) => {
      const typeMap = {
        'Vue.js': 'Web Application',
        'React': 'Web Application',
        'React Native': 'Mobile Application',
        'Three.js': 'Interactive Experience',
        'Node.js': 'Backend Service'
      }
      return typeMap[tag] || 'Project'
    }

    const formatStatLabel = (key) => {
      return key.charAt(0).toUpperCase() + key.slice(1)
    }

    // Image handling
    const handleImageError = (event) => {
      console.warn('Failed to load image:', event.target.src)
      event.target.style.display = 'none'
    }

    const handleImageLoad = (event) => {
      event.target.style.opacity = '1'
    }

    const openImageModal = (imageSrc) => {
      modalImage.value = imageSrc
      showImageModal.value = true
      document.body.style.overflow = 'hidden'
      stopAutoPlay() // Pause auto-play when modal is open
    }

    const closeImageModal = () => {
      showImageModal.value = false
      modalImage.value = ''
      document.body.style.overflow = 'auto'
      if (autoPlayEnabled.value) {
        startAutoPlay() // Resume auto-play when modal is closed
      }
    }

    // Keyboard navigation
    let keydownHandler
    
    onMounted(() => {
      keydownHandler = (event) => {
        if (showImageModal.value && event.key === 'Escape') {
          closeImageModal()
          return
        }
        
        switch (event.key) {
          case 'ArrowLeft':
            previousProjectWithPause()
            break
          case 'ArrowRight':
            nextProjectWithPause()
            break
          case ' ': // Spacebar to toggle auto-play
            event.preventDefault()
            toggleAutoPlay()
            break
        }
      }
      
      window.addEventListener('keydown', keydownHandler)
      
      // Start auto-play on mount
      if (autoPlayEnabled.value) {
        startAutoPlay()
      }
    })

    onUnmounted(() => {
      if (keydownHandler) {
        window.removeEventListener('keydown', keydownHandler)
      }
      stopAutoPlay()
      document.body.style.overflow = 'auto'
    })

    // Pause auto-play when page is not visible
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopAutoPlay()
      } else if (autoPlayEnabled.value && !showImageModal.value) {
        startAutoPlay()
      }
    }

    onMounted(() => {
      document.addEventListener('visibilitychange', handleVisibilityChange)
    })

    onUnmounted(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    })

    return {
      gl,
      currentIndex,
      isAnimating,
      projects,
      selectedProject,
      showImageModal,
      modalImage,
      autoPlayEnabled,
      getCardPosition,
      getCardRotation,
      getCardScale,
      getOverlayStyle,
      truncateText,
      selectProject,
      nextProject: nextProjectWithPause,
      previousProject: previousProjectWithPause,
      goToProject: goToProjectWithPause,
      toggleAutoPlay,
      openDemo,
      getProjectType,
      formatStatLabel,
      handleImageError,
      handleImageLoad,
      openImageModal,
      closeImageModal,
      Math
    }
  }
}
</script>
<style scoped>
.card-overlay.active{
  width: 320px;
}
.card-overlay.active .card-image-container{
  height: 220px;
}
 
.showcase-container {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
}

.canvas {
  width: 100%;
  height: 100%;
}

.cards-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 5;
}

.card-overlay {
  position: absolute;
  width: 220px;
  height: 320px; /* Increased height for image */
  pointer-events: auto;
  cursor: pointer;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.card-overlay.active .card-content {
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  transform: translateY(-5px);
}

.card-content {
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 15px;
  color: white;
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(15px);
  border: 2px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.card-content:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4);
}

/* Image Styles */
.card-image-container {
  position: relative;
  width: 100%;
  height: 140px;
  overflow: hidden;
  border-radius: 13px 13px 0 0;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.card-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.3;
}

.placeholder-icon {
  width: 40px;
  height: 40px;
  color: white;
}

.card-image-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  pointer-events: none;
}

.card-text-content {
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
}

.card-text-content h3 {
  margin: 0 0 10px 0;
  font-size: 1.2rem;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  line-height: 1.2;
}

.card-text-content p {
  font-size: 0.85rem;
  line-height: 1.4;
  margin: 0 0 15px 0;
  opacity: 0.9;
  flex-grow: 1;
}

.card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: auto;
}

.card-tag {
  padding: 4px 8px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 500;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

/* Enhanced Info Panel */
.ui-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
}

.project-info {
  position: absolute;
  top: 50px;
  left: 50px;
  max-width: 450px;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  transform: translateX(-100%);
  opacity: 0;
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: auto;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.project-info.active {
  transform: translateX(0);
  opacity: 1;
}

.info-header {
  display: flex;
  gap: 20px;
  padding: 30px 30px 20px 30px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.info-image-container {
  flex-shrink: 0;
}

.info-image {
  width: 80px;
  height: 80px;
  border-radius: 12px;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.info-image:hover {
  transform: scale(1.05);
}

.info-image-placeholder {
  width: 80px;
  height: 80px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.3;
}

.info-text h2 {
  color: white;
  margin: 0 0 10px 0;
  font-size: 1.8rem;
  font-weight: 700;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  line-height: 1.2;
}

.project-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.project-type {
  font-size: 0.9rem;
  font-weight: 500;
  opacity: 0.9;
}

.info-body {
  padding: 20px 30px 30px 30px;
}

.info-body p {
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  margin-bottom: 25px;
  font-size: 1rem;
}

.project-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.stat-item {
  text-align: center;
}

.stat-label {
  display: block;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  display: block;
  font-size: 1.2rem;
  font-weight: 600;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 25px;
}

.tag {
  color: white;
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.action-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.demo-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.demo-btn.primary {
  flex: 1;
  min-width: 140px;
}

.demo-btn.secondary {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.demo-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.4);
}

.demo-btn.secondary:hover {
  background: rgba(255, 255, 255, 0.2);
}

.btn-icon {
  width: 16px;
  height: 16px;
}

/* Enhanced Navigation */
.navigation {
  position: absolute;
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 25px;
  pointer-events: auto;
}

.nav-btn {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(15px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.nav-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  transform: scale(1.1);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4);
}

.nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.indicators {
  display: flex;
  gap: 12px;
}

.indicator {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  overflow: hidden;
  position: relative;
}

.indicator-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  opacity: 0.7;
  transition: opacity 0.3s ease;
}

.indicator.active {
  transform: scale(1.4);
  border-color: rgba(255, 255, 255, 0.5);
  box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
}

.indicator.active .indicator-image {
  opacity: 1;
}

.indicator:hover {
  background: rgba(255, 255, 255, 0.6);
  transform: scale(1.2);
}

.indicator:hover .indicator-image {
  opacity: 1;
}

/* Image Modal */
.image-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(10px);
  animation: modalFadeIn 0.3s ease;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.2);
  animation: modalSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes modalSlideIn {
  from {
    transform: scale(0.8) translateY(50px);
    opacity: 0;
  }
  to {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border-radius: 50%;
  cursor: pointer;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 1001;
  backdrop-filter: blur(10px);
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}

.modal-image {
  width: 100%;
  max-width: 800px;
  height: auto;
  display: block;
}

.modal-info {
  padding: 30px;
  color: white;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9), transparent);
}

.modal-info h3 {
  margin: 0 0 15px 0;
  font-size: 1.5rem;
  font-weight: 600;
}

.modal-info p {
  margin: 0;
  line-height: 1.6;
  opacity: 0.9;
}

/* Loading Animation */
.card-image {
  position: relative;
}

.card-image::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .project-info {
    top: 20px;
    left: 20px;
    right: 20px;
    max-width: none;
  }
  
  .info-header {
    padding: 20px 20px 15px 20px;
    flex-direction: column;
    text-align: center;
    gap: 15px;
  }
  
  .info-image-container {
    align-self: center;
  }
  
  .info-body {
    padding: 15px 20px 20px 20px;
  }
  
  .project-stats {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    padding: 15px;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .demo-btn {
    justify-content: center;
  }
  
  .navigation {
    bottom: 30px;
    gap: 15px;
  }
  
  .nav-btn {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }
  .nav-btn.auto-play {
  margin-left: 15px;
  background: rgba(0, 0, 0, 0.6);
}

.nav-btn.auto-play.active {
  background: rgba(76, 175, 80, 0.8);
  border-color: rgba(76, 175, 80, 0.5);
}

.nav-btn.auto-play:hover:not(:disabled) {
  background: rgba(76, 175, 80, 0.9);
}

.btn-icon {
  width: 20px;
  height: 20px;
}

/* Progress indicator for auto-play */
.navigation::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #4CAF50, #81C784);
  border-radius: 1px;
  animation: autoPlayProgress 4s linear infinite;
}

@keyframes autoPlayProgress {
  0% {
    width: 0;
  }
  100% {
    width: 200px;
  }
}

/* Hide progress bar when auto-play is disabled */
.navigation:not(.auto-play-active)::after {
  display: none;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .nav-btn.auto-play {
    margin-left: 10px;
    width: 45px;
    height: 45px;
  }
  
  .btn-icon {
    width: 16px;
    height: 16px;
  }
}
  .card-overlay {
    width: 180px;
    height: 260px;
  }
  
  .card-image-container {
    height: 100px;
  }
  
  .card-text-content {
    padding: 15px;
  }
  
  .card-text-content h3 {
    font-size: 1.1rem;
  }
  
  .card-text-content p {
    font-size: 0.8rem;
  }
  
  .indicator {
    width: 16px;
    height: 16px;
  }
  
  .modal-content {
    max-width: 95vw;
    max-height: 95vh;
    margin: 20px;
  }
  
  .modal-info {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .info-header {
    padding: 15px;
  }
  
  .info-body {
    padding: 10px 15px 15px 15px;
  }
  
  .info-text h2 {
    font-size: 1.5rem;
  }
  
  .project-stats {
    grid-template-columns: 1fr;
    text-align: left;
  }
  
  .stat-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  
  .stat-label {
    margin-bottom: 0;
  }
  
  .card-overlay {
    width: 160px;
    height: 220px;
  }
  
  .navigation {
    bottom: 20px;
    gap: 10px;
  }
  
  .indicators {
    gap: 8px;
  }
}

/* Dark mode enhancements */
@media (prefers-color-scheme: dark) {
  .card-content {
    background: rgba(0, 0, 0, 0.9);
  }
  
  .project-info {
    background: rgba(0, 0, 0, 0.9);
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .card-content {
    border: 3px solid rgba(255, 255, 255, 0.5);
  }
  
  .project-info {
    border: 3px solid rgba(255, 255, 255, 0.5);
  }
  
  .nav-btn {
    border: 3px solid rgba(255, 255, 255, 0.5);
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  .card-overlay,
  .project-info,
  .demo-btn,
  .nav-btn,
  .indicator {
    transition: none;
  }
  
  .modal-content {
    animation: none;
  }
  
  .image-modal {
    animation: none;
  }
}
</style>
